import { Grid, GridItem, HStack, Stack, Text } from "@chakra-ui/react";
import { FunctionComponent, useEffect, useState } from "react";
import { RawHtmlViewer } from "src/components/HtmlViewer";
import { useBranding } from "src/components/Providers/BrandingProvider";
import {
  mustacheSafeRender,
  mustacheSafeRenderHtml,
} from "src/services/mustache";
import * as EmailBody from "src/types/messages/clientBranding/emailBody";
import { AutomaticMessageFormType } from "../schemas";
import { usePartials } from "./partials";

type Errors = {
  emailSubjectError: string | undefined;
  emailMarkupError: string | undefined;
  emailTextError: string | undefined;
  smsBodyError: string | undefined;
};

type Content = {
  emailSubject: string;
  emailMarkup: string;
  emailText: string;
  smsBody: string;
};

type Props = {
  formValue: AutomaticMessageFormType;
  selectedMessageIndex: number;
};

export const PreviewMessageTemplate: FunctionComponent<Props> = ({
  formValue,
  selectedMessageIndex,
}) => {
  const partials = usePartials();

  const branding = useBranding();
  const [errors, setErrors] = useState<Errors>();
  const [content, setContent] = useState<Content>({
    emailSubject: "",
    emailMarkup: "",
    emailText: "",
    smsBody: "",
  });

  const message = formValue.messages[selectedMessageIndex];

  useEffect(() => {
    if (!branding || !partials || !message) return;

    const [emailSubjectError, emailSubject] = mustacheSafeRender(
      message.subject ?? "",
      partials,
      "One or more variables in the email subject are invalid. Correct or remove them and try again."
    );

    const [emailMarkupError, emailMarkup] = mustacheSafeRenderHtml(
      message.html ?? "",
      partials,
      "One or more variables in the email markup are invalid. Correct or remove them and try again."
    );

    const [emailTextError, emailText] = mustacheSafeRender(
      message.text ?? "",
      partials,
      "One or more variables in the email plain text are invalid. Correct or remove them and try again."
    );

    const [smsBodyError, smsBody] = mustacheSafeRender(
      message.smsText ?? "",
      partials,
      "One or more variables in the SMS message are invalid. Correct or remove them and try again."
    );

    setErrors({
      emailSubjectError,
      emailMarkupError,
      emailTextError,
      smsBodyError,
    });

    setContent({
      emailSubject: emailSubject,
      emailMarkup: EmailBody.template(branding, emailMarkup),
      emailText: emailText,
      smsBody: smsBody,
    });
  }, [formValue, branding, partials, message]);

  return (
    <Stack gap="5" flexGrow="1">
      <Stack>
        <Text fontSize="sm">Subject line</Text>
        {errors?.emailSubjectError && (
          <Text fontSize="sm" color="red">
            {errors.emailSubjectError}
          </Text>
        )}
        <Text>{content.emailSubject}</Text>
      </Stack>
      <Grid
        templateColumns="repeat(auto-fit, minmax(30rem, 1fr))"
        gap="5"
        templateRows="1fr"
        flexGrow="1"
      >
        <GridItem display="flex" flexGrow="1" flexDirection="column">
          <Stack minHeight="30rem" flexGrow="1">
            <Text fontSize="sm">HTML</Text>
            {errors?.emailMarkupError && (
              <Text fontSize="sm" color="red">
                {errors.emailMarkupError}
              </Text>
            )}
            <RawHtmlViewer html={content.emailMarkup} flexGrow="1" />
          </Stack>
        </GridItem>
        <GridItem
          display="flex"
          flexDirection="column"
          height="100%"
          flexWrap="nowrap"
        >
          <Stack height="100%">
            <Text fontSize="sm">Plain text</Text>
            {errors?.emailTextError && (
              <Text fontSize="sm" color="red">
                {errors.emailTextError}
              </Text>
            )}
            <Text as="pre" overflow="auto">
              {content.emailText}
            </Text>
          </Stack>
        </GridItem>
      </Grid>
      <Stack>
        <Text fontSize="sm">SMS message</Text>
        {errors?.smsBodyError && (
          <Text fontSize="sm" color="red">
            {errors.smsBodyError}
          </Text>
        )}
        <Text>{content.smsBody}</Text>
      </Stack>

      <HStack justifyContent="right"></HStack>
    </Stack>
  );
};
